import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';
import { ChevronRight } from 'lucide-react';

export interface DropdownMenuItem {
  id: string;
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  /** true이면 클릭 불가능한 섹션 헤더로 렌더링 */
  isSection?: boolean;
  /** 서브메뉴 항목 — 마우스 호버 시 오른쪽에 펼쳐짐 */
  children?: DropdownMenuItem[];
}

interface DropdownMenuProps {
  open: boolean;
  x: number;
  y: number;
  items: DropdownMenuItem[];
  onClose: () => void;
  direction?: 'down' | 'up';
  align?: 'left' | 'right';
  width?: number;
  minWidth?: number;
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 120;
`;

const Menu = styled.div<{
  $x: number;
  $y: number;
  $direction: 'down' | 'up';
  $align: 'left' | 'right';
  $width?: number;
  $minWidth?: number;
}>`
  position: fixed;
  top: ${({ $y }) => $y}px;
  left: ${({ $x }) => $x}px;
  z-index: 121;
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  min-width: ${({ $minWidth }) => ($minWidth ? `${$minWidth}px` : '180px')};
  max-width: calc(100vw - 16px);
  padding: 6px;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(40,40,42,0.92)' : 'rgba(255,255,255,0.92)'};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.45), 0 1px 3px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)'};

  ${({ $direction, $align }) => {
    const tx = $align === 'right' ? '-100%' : '0';
    const ty = $direction === 'up' ? '-100%' : '0';
    return css`
      transform: translate(${tx}, ${ty});
    `;
  }}
`;

const ItemWrapper = styled.div`
  position: relative;
`;

const ItemButton = styled.button<{ $danger?: boolean; $hasChildren?: boolean; $focused?: boolean }>`
  width: 100%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 6px 10px;
  border-radius: 8px;
  color: ${({ theme, $danger }) => ($danger ? theme.colors.error : theme.colors.textPrimary)};
  cursor: pointer;
  font-size: 0.8125rem;
  text-align: left;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)'};
  }

  ${({ $focused, theme }) =>
    $focused &&
    css`
      background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)'};
    `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ItemLabel = styled.span`
  flex: 1;
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-left: auto;
`;

const SubMenu = styled.div<{ $direction: 'up' | 'down' }>`
  position: absolute;
  left: 100%;
  ${({ $direction }) =>
    $direction === 'up'
      ? css`bottom: 0;`
      : css`top: 0;`}
  min-width: 170px;
  max-height: 420px;
  overflow-y: auto;
  padding: 6px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(40,40,42,0.92)' : 'rgba(255,255,255,0.92)'};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.45), 0 1px 3px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)'};
  z-index: 122;
`;

const SectionHeader = styled.div`
  padding: 6px 10px 4px;
  font-size: 0.7rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: none;
  letter-spacing: 0;
  user-select: none;
`;

/* ---- 서브메뉴가 있는 아이템 ---- */

const SubMenuItem: React.FC<{
  item: DropdownMenuItem;
  direction: 'up' | 'down';
  onClose: () => void;
  isPinned: boolean;
  hasClickPinnedSubmenu: boolean;
  onHoverPin: () => void;
  onClickPin: () => void;
  isTopFocused: boolean;
  focusedChildIndex: number | null;
  onFocusTop: () => void;
  onFocusChild: (index: number) => void;
}> = ({
  item,
  direction,
  onClose,
  isPinned,
  hasClickPinnedSubmenu,
  onHoverPin,
  onClickPin,
  isTopFocused,
  focusedChildIndex,
  onFocusTop,
  onFocusChild,
}) => {
  const [hovered, setHovered] = useState(false);
  const subMenuOpen = isPinned || (!hasClickPinnedSubmenu && hovered);

  return (
    <ItemWrapper
      onMouseEnter={() => {
        setHovered(true);
        onFocusTop();
        if (!item.disabled) {
          onHoverPin();
        }
      }}
      onMouseLeave={() => setHovered(false)}
    >
      <ItemButton
        $danger={item.danger}
        disabled={item.disabled}
        $hasChildren
        $focused={isTopFocused}
        onClick={() => {
          if (item.disabled) return;
          onClickPin();
        }}
      >
        {item.icon}
        <ItemLabel>{item.label}</ItemLabel>
        <ChevronIcon><ChevronRight size={14} /></ChevronIcon>
      </ItemButton>
      {subMenuOpen && item.children && (
        <SubMenu $direction={direction}>
          {item.children.map((child, index) =>
            child.isSection ? (
              <SectionHeader key={child.id}>{child.label}</SectionHeader>
            ) : (
              <ItemButton
                key={child.id}
                $danger={child.danger}
                disabled={child.disabled}
                $focused={focusedChildIndex === index}
                onMouseEnter={() => {
                  onFocusTop();
                  onFocusChild(index);
                }}
                onClick={() => {
                  if (child.disabled) return;
                  child.onClick();
                  onClose();
                }}
              >
                {child.icon}
                <ItemLabel>{child.label}</ItemLabel>
              </ItemButton>
            ),
          )}
        </SubMenu>
      )}
    </ItemWrapper>
  );
};

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  open,
  x,
  y,
  items,
  onClose,
  direction = 'down',
  align = 'left',
  width,
  minWidth,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pinnedSubmenuId, setPinnedSubmenuId] = useState<string | null>(null);
  const [pinnedByClick, setPinnedByClick] = useState(false);
  const [focusedParentIndex, setFocusedParentIndex] = useState<number>(-1);
  const [focusedChildIndex, setFocusedChildIndex] = useState<number | null>(null);

  const isNavigableItem = useCallback((item: DropdownMenuItem) => {
    if (item.isSection) return false;
    if (item.disabled) return false;
    return true;
  }, []);

  const getNavigableParentIndices = useCallback(() => {
    const indices: number[] = [];
    items.forEach((item, idx) => {
      if (isNavigableItem(item)) indices.push(idx);
    });
    return indices;
  }, [items, isNavigableItem]);

  const getNavigableChildIndices = useCallback((parentIdx: number) => {
    const parent = items[parentIdx];
    if (!parent?.children?.length) return [] as number[];
    const indices: number[] = [];
    parent.children.forEach((child, idx) => {
      if (isNavigableItem(child)) indices.push(idx);
    });
    return indices;
  }, [items, isNavigableItem]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setPinnedSubmenuId(null);
      setPinnedByClick(false);
      setFocusedParentIndex(-1);
      setFocusedChildIndex(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const parentIndices = getNavigableParentIndices();
    setFocusedParentIndex(parentIndices[0] ?? -1);
    setFocusedChildIndex(null);
    setPinnedSubmenuId(null);
    setPinnedByClick(false);
    const raf = window.requestAnimationFrame(() => {
      menuRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [open, getNavigableParentIndices]);

  const focusParentIndex = useCallback((idx: number) => {
    setFocusedParentIndex(idx);
    setFocusedChildIndex(null);
  }, []);

  const handleMenuKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') {
      onClose();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }

    const parentIndices = getNavigableParentIndices();
    if (parentIndices.length === 0) return;
    const currentParent = parentIndices.includes(focusedParentIndex)
      ? focusedParentIndex
      : parentIndices[0];
    const currentItem = items[currentParent];
    const childIndices = getNavigableChildIndices(currentParent);
    const inChildMode =
      focusedChildIndex !== null && childIndices.includes(focusedChildIndex);
    const isSelectKey = e.key === 'Enter' || e.key === ' ';

    if (inChildMode) {
      const childPos = childIndices.indexOf(focusedChildIndex);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = childIndices[(childPos + 1) % childIndices.length];
        setFocusedChildIndex(next ?? null);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const next = childIndices[(childPos - 1 + childIndices.length) % childIndices.length];
        setFocusedChildIndex(next ?? null);
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setFocusedChildIndex(null);
        return;
      }
      if (isSelectKey) {
        e.preventDefault();
        const child = currentItem.children?.[focusedChildIndex];
        if (child && isNavigableItem(child)) {
          child.onClick();
          onClose();
        }
      }
      return;
    }

    const parentPos = parentIndices.indexOf(currentParent);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = parentIndices[(parentPos + 1) % parentIndices.length];
      focusParentIndex(next);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = parentIndices[(parentPos - 1 + parentIndices.length) % parentIndices.length];
      focusParentIndex(next);
      return;
    }
    if (e.key === 'ArrowRight') {
      if (childIndices.length > 0) {
        e.preventDefault();
        setPinnedSubmenuId(currentItem.id);
        setFocusedChildIndex(childIndices[0] ?? null);
      }
      return;
    }
    if (isSelectKey) {
      e.preventDefault();
      if (childIndices.length > 0) {
        setPinnedSubmenuId(currentItem.id);
        setFocusedChildIndex(childIndices[0] ?? null);
      } else if (isNavigableItem(currentItem)) {
        currentItem.onClick();
        onClose();
      }
    }
  }, [
    focusedChildIndex,
    focusedParentIndex,
    focusParentIndex,
    getNavigableChildIndices,
    getNavigableParentIndices,
    isNavigableItem,
    items,
    onClose,
  ]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      <Backdrop onClick={onClose} />
      <Menu
        ref={menuRef}
        tabIndex={-1}
        onKeyDown={handleMenuKeyDown}
        $x={x}
        $y={y}
        $direction={direction}
        $align={align}
        $width={width}
        $minWidth={minWidth}
      >
        {items.map((item, index) =>
          item.children && item.children.length > 0 ? (
            <SubMenuItem
              key={item.id}
              item={item}
              direction={direction}
              onClose={onClose}
              isPinned={pinnedSubmenuId === item.id}
              hasClickPinnedSubmenu={pinnedByClick && !!pinnedSubmenuId}
              onHoverPin={() => {
                if (pinnedByClick) return;
                setPinnedSubmenuId(item.id);
              }}
              onClickPin={() => {
                setPinnedSubmenuId(item.id);
                setPinnedByClick(true);
              }}
              isTopFocused={focusedParentIndex === index}
              focusedChildIndex={focusedParentIndex === index ? focusedChildIndex : null}
              onFocusTop={() => focusParentIndex(index)}
              onFocusChild={(childIndex) => {
                setFocusedParentIndex(index);
                setFocusedChildIndex(childIndex);
              }}
            />
          ) : (
            <ItemButton
              key={item.id}
              $danger={item.danger}
              disabled={item.disabled}
              $focused={focusedParentIndex === index}
              onMouseEnter={() => {
                focusParentIndex(index);
              }}
              onClick={() => {
                if (item.disabled) return;
                item.onClick();
                onClose();
              }}
            >
              {item.icon}
              <ItemLabel>{item.label}</ItemLabel>
            </ItemButton>
          ),
        )}
      </Menu>
    </>,
    document.body,
  );
};
